import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase, Venue } from '../lib/supabase';
import { useEmberStore } from '../lib/store';
import { toast } from '../lib/toast';
import { compressImage, isImageFile } from '../lib/imageCompression';
import BottomNav from '../components/BottomNav';
import CheckInLocationGrid from '../components/CheckInLocationGrid';
import LocationMapPicker from '../components/LocationMapPicker';
import Button from '../components/ui/Button';
import { ArrowLeft, Calendar, MapPin, FileText, Map, Image as ImageIcon } from 'lucide-react';
import { loadDraftState, saveDraftState, clearDraftState } from '../lib/draftStateStore';
import { dataUrlToFile } from '../lib/draftPhotoStore';
import { usePhotoStorage, StoredPhoto } from '../hooks/usePhotoStorage';

const EVENT_DRAFT_KEY = 'ember.eventDraft.v1';

interface EventDraftStored {
  title: string;
  description: string;
  startDate: string;
  startTime: string;
  endDate: string;
  endTime: string;
  maxAttendees: number | null;
  venueName: string;
  selectedLocation: {
    name: string;
    address: string;
    latitude: number;
    longitude: number;
    venue_id?: string;
  } | null;
  isPublic: boolean;
  photoPreview: string | null;
  photoName: string | null;
  photoType: string | null;
}

export default function CreateEvent() {
  const navigate = useNavigate();
  const { currentUser } = useEmberStore();
  const draftLoadedRef = useRef(false);
  const { loadPhoto, loadPhotoSync, savePhoto, savePhotoData, clearPhoto } = usePhotoStorage(EVENT_DRAFT_KEY);
  const initialStoredPhoto = loadPhotoSync();
  const [photoData, setPhotoData] = useState<StoredPhoto | null>(initialStoredPhoto);

  // Basic info
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [startDate, setStartDate] = useState('');
  const [startTime, setStartTime] = useState('');
  const [endDate, setEndDate] = useState('');
  const [endTime, setEndTime] = useState('');
  const [maxAttendees, setMaxAttendees] = useState<number | ''>('');
  
  // Location selection
  const [venueName, setVenueName] = useState('');
  const [selectedLocation, setSelectedLocation] = useState<{
    name: string;
    address: string;
    latitude: number;
    longitude: number;
    venue_id?: string;
  } | null>(null);
  const [showMapPicker, setShowMapPicker] = useState(false);
  
  // Data loading
  const [userCheckIns, setUserCheckIns] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  
  // Photo upload
  const [photoFile, setPhotoFile] = useState<File | null>(() =>
    initialStoredPhoto ? dataUrlToFile(initialStoredPhoto.data, initialStoredPhoto.name, initialStoredPhoto.type) : null
  );
  const [photoPreview, setPhotoPreview] = useState<string | null>(initialStoredPhoto ? initialStoredPhoto.data : null);
  const [compressing, setCompressing] = useState(false);
  
  // Event visibility
  const [isPublic, setIsPublic] = useState(true);

  useEffect(() => {
    loadUserCheckIns();
  }, [currentUser]);

  useEffect(() => {
    let isActive = true;

    (async () => {
      try {
        const stored = await loadDraftState<EventDraftStored>(EVENT_DRAFT_KEY);
        if (!isActive) {
          return;
        }

        if (stored) {
          if (typeof stored.title === 'string') setTitle(stored.title);
          if (typeof stored.description === 'string') setDescription(stored.description);
          if (typeof stored.startDate === 'string') setStartDate(stored.startDate);
          if (typeof stored.startTime === 'string') setStartTime(stored.startTime);
          if (typeof stored.endDate === 'string') setEndDate(stored.endDate);
          if (typeof stored.endTime === 'string') setEndTime(stored.endTime);
          if (typeof stored.maxAttendees === 'number') {
            setMaxAttendees(stored.maxAttendees);
          } else {
            setMaxAttendees('');
          }
          if (typeof stored.venueName === 'string') setVenueName(stored.venueName);
          if (stored.selectedLocation) setSelectedLocation(stored.selectedLocation);
          if (typeof stored.isPublic === 'boolean') setIsPublic(stored.isPublic);

          if (stored.photoPreview) {
            const restored: StoredPhoto = {
              name: stored.photoName && stored.photoName.trim() ? stored.photoName : 'event-photo.jpg',
              type: stored.photoType && stored.photoType.trim() ? stored.photoType : 'image/jpeg',
              data: stored.photoPreview,
              processed: true,
              processedAt: new Date().toISOString(),
            };
            await savePhotoData(restored);
            if (isActive) {
              setPhotoData(restored);
            }
          } else {
            const restoredPhoto = await loadPhoto();
            if (isActive && restoredPhoto) {
              setPhotoData(restoredPhoto);
            }
          }
        } else {
          const restoredPhoto = await loadPhoto();
          if (isActive && restoredPhoto) {
            setPhotoData(restoredPhoto);
          }
        }
      } catch (error) {
        console.warn('Failed to load event draft', error);
      } finally {
        if (isActive) {
          draftLoadedRef.current = true;
        }
      }
    })();

    return () => {
      isActive = false;
    };
  }, [loadPhoto, savePhotoData]);

  useEffect(() => {
    if (!draftLoadedRef.current) {
      return;
    }

    const draft: EventDraftStored = {
      title,
      description,
      startDate,
      startTime,
      endDate,
      endTime,
      maxAttendees: typeof maxAttendees === 'number' ? maxAttendees : null,
      venueName,
      selectedLocation,
      isPublic,
      photoPreview: photoData?.data ?? null,
      photoName: photoData?.name ?? null,
      photoType: photoData?.type ?? null,
    };

    saveDraftState(EVENT_DRAFT_KEY, draft).catch((error) => {
      console.warn('Failed to save event draft', error);
    });
  }, [
    title,
    description,
    startDate,
    startTime,
    endDate,
    endTime,
    maxAttendees,
    venueName,
    selectedLocation,
    isPublic,
    photoData,
  ]);

  useEffect(() => {
    let cancelled = false;

    if (!photoData) {
      setPhotoPreview(null);
      setPhotoFile(null);
      setCompressing(false);
      return;
    }

    setPhotoPreview(photoData.data);
    setCompressing(true);

    (async () => {
      try {
        const restoredFile = dataUrlToFile(
          photoData.data,
          photoData.name && photoData.name.trim() ? photoData.name : 'event-photo.jpg',
          photoData.type || 'image/jpeg'
        );
        if (!restoredFile) {
          throw new Error('Failed to reconstruct event photo from stored data');
        }
        const compressed = await compressImage(restoredFile, {
          maxWidth: 1920,
          maxHeight: 1920,
          quality: 0.85,
          outputFormat: 'image/jpeg',
        });
        if (!cancelled) {
          setPhotoFile(compressed);
        }
      } catch (error) {
        if (!cancelled) {
          console.error('Failed to prepare event photo', error);
          toast.error('Failed to process image');
          setPhotoFile(null);
        }
      } finally {
        if (!cancelled) {
          setCompressing(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [photoData]);

  async function loadUserCheckIns() {
    if (!currentUser) return;
    
    try {
      const { data, error } = await supabase
        .from('checkins')
        .select(`
          *,
          venue:venues(*)
        `)
        .eq('user_id', currentUser.id)
        .order('created_at', { ascending: false });

      if (error) throw error;
      
      // Transform data to include venue info at top level
      const transformed = data?.map(checkIn => ({
        ...checkIn,
        venue_id: checkIn.venue_id,
        venue_name: checkIn.venue?.name,
        venue_address: checkIn.venue?.address,
        venue_latitude: checkIn.venue?.latitude,
        venue_longitude: checkIn.venue?.longitude
      })) || [];
      
      setUserCheckIns(transformed);
    } catch (err) {
      console.error('Error loading check-ins:', err);
    }
  }

  const handleLocationSelected = (location: {
    name: string;
    address: string;
    latitude: number;
    longitude: number;
    venue_id?: string;
  }) => {
    setSelectedLocation(location);
  };

  async function handlePhotoChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) {
      setPhotoData(null);
      setPhotoFile(null);
      setPhotoPreview(null);
      clearPhoto();
      setCompressing(false);
      return;
    }

    if (!isImageFile(file)) {
      toast.error('Please select a valid image file');
      return;
    }

    setCompressing(true);
    try {
      const stored = await savePhoto(file);
      if (stored) {
        setPhotoData(stored);
      } else {
        setPhotoData(null);
        setCompressing(false);
      }
    } catch (error) {
      console.error('Failed to save event photo', error);
      toast.error('Failed to process image');
      setPhotoData(null);
      setCompressing(false);
    }
  }

  async function handleSubmit() {
    // Validation
    if (!title.trim()) {
      toast.error('Please enter an event title');
      return;
    }
    if (!description.trim()) {
      toast.error('Please enter an event description');
      return;
    }
    if (!startDate || !startTime) {
      toast.error('Please select start date and time');
      return;
    }
    if (!venueName.trim()) {
      toast.error('Please enter a venue/location name');
      return;
    }
    if (!selectedLocation) {
      toast.error('Please set the event location on the map');
      return;
    }

    setLoading(true);
    try {
      const startDateTime = new Date(`${startDate}T${startTime}`).toISOString();
      const endDateTime = endDate && endTime ? new Date(`${endDate}T${endTime}`).toISOString() : null;

      // Upload photo if present
      let photo_url: string | null = null;
      let uploadFile = photoFile;
      if (!uploadFile && photoData) {
        const fallbackFile = dataUrlToFile(
          photoData.data,
          photoData.name && photoData.name.trim() ? photoData.name : 'event-photo.jpg',
          photoData.type || 'image/jpeg'
        );
        if (fallbackFile) {
          uploadFile = fallbackFile;
        }
      }
      if (uploadFile) {
        const filePath = `${currentUser?.id}/${Date.now()}-${uploadFile.name}`;
        const { data: up, error: upErr } = await supabase.storage
          .from('event-photos')
          .upload(filePath, uploadFile, { upsert: false });
        if (!upErr && up) {
          const { data: pub } = supabase.storage
            .from('event-photos')
            .getPublicUrl(up.path);
          photo_url = pub?.publicUrl ?? null;
        }
      }

      // Store coordinates directly in events table (no venue creation)
      const { data, error } = await supabase
        .from('events')
        .insert({
          creator_id: currentUser?.id,
          title: title.trim(),
          description: description.trim(),
          venue_name: venueName.trim(),
          latitude: selectedLocation.latitude,
          longitude: selectedLocation.longitude,
          start_datetime: startDateTime,
          end_datetime: endDateTime,
          max_attendees: typeof maxAttendees === 'number' ? maxAttendees : null,
          photo_url,
          is_public: isPublic,
        })
        .select()
        .single();

      if (error) throw error;
      
      toast.success('Event created successfully!');
      draftLoadedRef.current = false;
      await clearDraftState(EVENT_DRAFT_KEY);
      clearPhoto();
      setPhotoData(null);
      setPhotoFile(null);
      setPhotoPreview(null);
      setTitle('');
      setDescription('');
      setStartDate('');
      setStartTime('');
      setEndDate('');
      setEndTime('');
      setMaxAttendees('');
      setVenueName('');
      setSelectedLocation(null);
      setIsPublic(true);
      setShowMapPicker(false);
      navigate('/discover');
    } catch (err) {
      console.error('Error creating event:', err);
      toast.error('Failed to create event. Please try again.');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div style={{ minHeight: '100vh', paddingBottom: '120px' }}>
      {/* Header */}
      <header style={{
        padding: '1rem',
        borderBottom: '1px solid #1f1f1f',
        display: 'flex',
        alignItems: 'center',
        gap: '0.75rem',
        position: 'sticky',
        top: 0,
        backgroundColor: '#0b0b0b',
        zIndex: 10,
      }}>
        <button
          onClick={() => navigate('/discover')}
          style={{ background: 'none', border: 'none', color: 'var(--color-accent)', cursor: 'pointer' }}
        >
          <ArrowLeft size={24} />
        </button>
        <Calendar size={24} color="var(--color-accent)" />
        <h1 style={{ fontSize: '1.5rem', letterSpacing: '0.02em', textTransform: 'uppercase' }}>
          Create Event
        </h1>
      </header>

      <div className="container" style={{ paddingTop: '1.5rem' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>

          {/* Event Title */}
            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                Event Title *
              </label>
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="e.g., Pipe Smoking Book Club, Monthly Meetup"
                style={{ width: '100%' }}
                required
              />
            </div>

            {/* Event Description */}
            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                <FileText size={16} style={{ display: 'inline', marginRight: '0.5rem' }} />
                Description *
              </label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Describe what this event is about, what to expect, what to bring..."
                rows={4}
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  backgroundColor: 'var(--color-card)',
                  border: '1px solid var(--color-border)',
                  borderRadius: '6px',
                  color: 'white',
                  fontSize: '0.875rem',
                  resize: 'vertical',
                  fontFamily: 'inherit'
                }}
                required
              />
            </div>

            {/* Start Date & Time */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                  Start Date *
                </label>
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  style={{ width: '100%' }}
                  min={new Date().toISOString().split('T')[0]}
                  required
                />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                  Start Time *
                </label>
                <input
                  type="time"
                  value={startTime}
                  onChange={(e) => setStartTime(e.target.value)}
                  style={{ width: '100%' }}
                  required
                />
              </div>
            </div>

            {/* End Date & Time (Optional) */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                  End Date (Optional)
                </label>
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  style={{ width: '100%' }}
                  min={startDate || new Date().toISOString().split('T')[0]}
                />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                  End Time (Optional)
                </label>
                <input
                  type="time"
                  value={endTime}
                  onChange={(e) => setEndTime(e.target.value)}
                  style={{ width: '100%' }}
                />
              </div>
            </div>

            {/* Max Attendees */}
            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                Max Attendees (Optional)
              </label>
              <input
                type="number"
                value={maxAttendees}
                onChange={(e) => setMaxAttendees(e.target.value ? parseInt(e.target.value) : '')}
                placeholder="Leave empty for unlimited"
                min="1"
                style={{ width: '100%' }}
              />
            </div>

          {/* Venue Name Input */}
            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                <MapPin size={16} style={{ display: 'inline', marginRight: '0.5rem' }} />
                Venue/Location Name *
              </label>
              <input
                type="text"
                value={venueName}
                onChange={(e) => setVenueName(e.target.value)}
                placeholder="e.g., The Pipe Lounge, Starbucks, My Backyard"
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  backgroundColor: 'var(--color-card)',
                  border: '1px solid var(--color-border)',
                  borderRadius: '6px',
                  color: 'white',
                  fontSize: '0.875rem',
                  fontFamily: 'inherit'
                }}
                required
              />
            </div>

            {/* Location Selection */}
            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                Set Location *
              </label>
              
              {!selectedLocation ? (
                <button
                  type="button"
                  onClick={() => setShowMapPicker(true)}
                  style={{
                    width: '100%',
                    padding: '1rem',
                    backgroundColor: 'var(--color-card)',
                    border: '2px dashed var(--color-accent)',
                    borderRadius: '8px',
                    color: 'var(--color-accent)',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '0.5rem',
                    fontSize: '0.875rem',
                    fontWeight: '600'
                  }}
                >
                  <Map size={20} />
                  Click to Set Location on Map
                </button>
              ) : (
                <div style={{
                  padding: '1rem',
                  backgroundColor: 'rgba(63, 160, 201, 0.1)',
                  border: '2px solid var(--color-secondary)',
                  borderRadius: '8px'
                }}>
                  <div style={{ display: 'flex', alignItems: 'start', gap: '0.75rem', marginBottom: '0.75rem' }}>
                    <MapPin size={20} style={{ color: 'var(--color-secondary)', flexShrink: 0, marginTop: '0.25rem' }} />
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: '600', marginBottom: '0.25rem', color: 'var(--color-secondary)' }}>
                        📍 Location Set
                      </div>
                      <div style={{ fontSize: '0.875rem', color: '#cfcfcf', marginBottom: '0.25rem' }}>
                        {selectedLocation.address}
                      </div>
                      <div style={{ fontSize: '0.75rem', color: '#999' }}>
                        {selectedLocation.latitude.toFixed(6)}, {selectedLocation.longitude.toFixed(6)}
                      </div>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => setShowMapPicker(true)}
                    style={{
                      width: '100%',
                      padding: '0.5rem',
                      backgroundColor: 'transparent',
                      border: '1px solid var(--color-secondary)',
                      borderRadius: '6px',
                      color: 'var(--color-secondary)',
                      cursor: 'pointer',
                      fontSize: '0.875rem',
                      fontWeight: '500'
                    }}
                  >
                    Change Location
                  </button>
                </div>
              )}
            </div>

          {/* Event Photo Upload */}
          <div>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
              Event Photo (Optional)
            </label>

            {/* Preview */}
            {photoPreview ? (
              <div style={{ marginBottom: '0.75rem' }}>
                <img
                  src={photoPreview}
                  alt="Event Preview"
                  style={{
                    width: '100%',
                    maxHeight: '300px',
                    objectFit: 'contain',
                    borderRadius: '8px',
                    backgroundColor: '#1f1f1f',
                  }}
                />
              </div>
            ) : compressing ? (
              <div style={{ 
                padding: '2rem', 
                textAlign: 'center',
                border: '2px dashed #333',
                borderRadius: '8px',
                marginBottom: '0.75rem'
              }}>
                <div className="spinner" style={{ margin: '0 auto 0.5rem', width: '32px', height: '32px' }}></div>
                <span style={{ color: '#999' }}>Compressing image...</span>
              </div>
            ) : null}
            
            {/* Upload Buttons */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
              <label
                htmlFor="event-photo-gallery"
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '0.5rem',
                  padding: '1rem',
                  border: '2px dashed #333',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  backgroundColor: 'var(--color-card)',
                  transition: 'border-color 0.2s',
                }}
              >
                <ImageIcon size={24} color="var(--color-accent)" />
                <span style={{ fontSize: '0.875rem', textAlign: 'center' }}>Choose from Gallery</span>
              </label>

              <label
                htmlFor="event-photo-camera"
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '0.5rem',
                  padding: '1rem',
                  border: '2px dashed #333',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  backgroundColor: 'var(--color-card)',
                  transition: 'border-color 0.2s',
                }}
              >
                <ImageIcon size={24} color="var(--color-accent)" />
                <span style={{ fontSize: '0.875rem', textAlign: 'center' }}>Take Photo</span>
              </label>
            </div>

            <input
              id="event-photo-gallery"
              type="file"
              accept="image/*"
              onChange={handlePhotoChange}
              style={{ display: 'none' }}
            />

            <input
              id="event-photo-camera"
              type="file"
              accept="image/*"
              capture="environment"
              onChange={handlePhotoChange}
              style={{ display: 'none' }}
            />
          </div>

          {/* Event Visibility Toggle */}
          <div>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
              Event Visibility
            </label>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '1rem',
              backgroundColor: isPublic ? 'rgba(196, 255, 0, 0.1)' : 'var(--color-card)',
              border: `2px solid ${isPublic ? 'var(--color-accent)' : 'var(--color-border)'}`,
              borderRadius: '8px'
            }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: '600', marginBottom: '0.25rem' }}>
                  {isPublic ? '🌍 Public Event' : '🔒 Private Event'}
                </div>
                <div style={{ fontSize: '0.875rem', color: '#999' }}>
                  {isPublic 
                    ? 'Visible to all users. Nearby users will be notified.' 
                    : 'Only visible to invited users. No proximity notifications sent.'
                  }
                </div>
              </div>
              <button
                type="button"
                aria-pressed={isPublic}
                onClick={() => setIsPublic(!isPublic)}
                style={{
                  alignSelf: 'center',
                  minWidth: '90px',
                  height: '28px',
                  padding: '0 10px',
                  backgroundColor: isPublic ? 'var(--color-accent)' : '#666',
                  color: '#111',
                  border: 'none',
                  borderRadius: '14px',
                  cursor: 'pointer',
                  transition: 'background-color 0.2s ease, color 0.2s ease',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '0.85rem',
                  fontWeight: 600,
                  marginLeft: '1rem'
                }}
              >
                {isPublic ? 'Public' : 'Private'}
              </button>
            </div>
            
            {isPublic && (
              <div style={{
                marginTop: '0.75rem',
                padding: '0.75rem',
                backgroundColor: 'rgba(196, 255, 0, 0.05)',
                border: '1px solid rgba(196, 255, 0, 0.2)',
                borderRadius: '6px',
                fontSize: '0.875rem',
                color: '#cfcfcf'
              }}>
                <strong style={{ color: 'var(--color-accent)' }}>📢 Public Event Notifications</strong>
                <br />
                • Users within their notification radius will be alerted
                <br />
                • Event will appear on the map and discover page
                <br />
                • Anyone can view and respond to this event
              </div>
            )}
          </div>

          {/* Submit Button */}
          <Button
            onClick={handleSubmit}
            disabled={!venueName.trim() || !selectedLocation || compressing}
            loading={loading}
            variant="primary"
            fullWidth
          >
            Create Event
          </Button>
        </div>

        {/* Map Picker Modal */}
        {showMapPicker && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.8)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: '1rem'
          }}>
            <div style={{
              backgroundColor: '#1a1a1a',
              borderRadius: '12px',
              width: '100%',
              maxWidth: '800px',
              height: '80vh',
              display: 'flex',
              flexDirection: 'column',
              border: '1px solid var(--color-border)',
              overflow: 'hidden'
            }}>
              {/* Modal Header */}
              <div style={{
                padding: '1rem',
                borderBottom: '1px solid var(--color-border)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between'
              }}>
                <h3 style={{ fontSize: '1.25rem', fontWeight: '600' }}>Choose Event Location</h3>
                <button
                  onClick={() => setShowMapPicker(false)}
                  style={{
                    padding: '0.5rem',
                    backgroundColor: 'transparent',
                    border: 'none',
                    color: '#999',
                    cursor: 'pointer',
                    fontSize: '1.5rem'
                  }}
                >
                  ×
                </button>
              </div>

              {/* Map Content */}
              <div style={{ flex: 1, padding: '1rem', minHeight: 0, overflow: 'auto' }}>
                <LocationMapPicker
                  onLocationSelected={(location) => {
                    setSelectedLocation({
                      ...location,
                      name: venueName || location.name
                    });
                    setShowMapPicker(false);
                  }}
                />
              </div>
            </div>
          </div>
        )}
      </div>

      <BottomNav />
    </div>
  );
}

